import React, { ComponentProps } from 'react'
import { Spin } from 'antd'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useCalculationTimeIntervalValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { getColorByIndex } from '../../util/colors'
import { getFontSize } from '../../util/fontSize'
import { BORDER_WIDTH } from '../../util/constants'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TITLE = 'Andel beräkningar inom intervall av beräknad tid'

// Alla möjliga intervall i korrekt ordning (sort_order 1–7)
const INTERVAL_ORDER = ['0–5h', '5–10h', '10–25h', '25–50h', '50–100h', '100–150h', '150h+']

export const CalculationTimeInterval: React.FC<{ text: React.ReactNode }> = ({ text }) => {
  const { data, isLoading, error } = useCalculationTimeIntervalValues()

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} title={TITLE} />
  }

  if (error || !data) {
    return (
      <BenchmarkingContentRow
        title={TITLE}
        text={text}
        graph={
          <div className="flex h-full w-full items-center justify-center font-mono text-sm text-red-600">
            <span>{error ? `Fel vid hämtning av data: ${String(error)}` : 'Ingen data tillgänglig'}</span>
          </div>
        }
      />
    )
  }

  const municipalities = Array.from(new Set(data.map((r) => r.municipality_name as string))).sort()

  // Filtrera till de intervall som faktiskt finns i data (hoppa över tomma)
  const intervalsInData = new Set(data.map((r) => r.interval as string))
  const intervals = INTERVAL_ORDER.filter((i) => intervalsInData.has(i))

  // X-axel = kommuner, ett lager (dataset) per intervall — staplarna summerar till 100 %
  const datasets = intervals.map((interval, i) => {
    const color = getColorByIndex(i)
    return {
      label: interval,
      backgroundColor: color.fill,
      borderColor: color.border,
      borderWidth: BORDER_WIDTH,
      data: municipalities.map((name) => {
        const row = data.find((r) => r.municipality_name === name && r.interval === interval)
        return row ? Number(row.proportion_pct) : 0
      }),
    }
  })

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { font: { size: getFontSize('md') } },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: { font: { size: getFontSize('md') }, callback: (v) => `${v}%` },
        title: { display: true, text: 'Andel beräkningar (%)', font: { size: getFontSize('md') } },
      },
    },
  }

  // Legend visar intervallen (inte kommunerna)
  const legend = (
    <BenchmarkingLegend
      items={intervals.map((interval, i) => {
        const color = getColorByIndex(i)
        return { text: interval, fillColor: color.fill, borderColor: color.border }
      })}
    />
  )

  return <BenchmarkingContentRow title={TITLE} text={text} legend={legend} graph={<Bar data={{ labels: municipalities, datasets }} options={options} />} />
}
